import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../Icon";
import "./Card.css";

/* =========================================================
   디지털 명함 — /<slug>_businesscard (예: /terry_businesscard)
   QR 코드 · NFC 태그 · 링크 전달이 모두 이 주소로 들어온다.
   사이트 헤더·푸터 없이 휴대폰 한 화면에 맞춘 단독 페이지.
   데이터와 연락처(vCard)·QR 은 백엔드 app/cards.py 가 만든다.
   ========================================================= */

const base = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: "1.6",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  width: 20,
  height: 20,
  "aria-hidden": "true",
  focusable: "false",
};

const Ico = {
  Chat: () => (
    <svg {...base}>
      <path d="M4 5h16v11H9l-5 4z" />
    </svg>
  ),
  UserPlus: () => (
    <svg {...base}>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c.8-3.6 3.4-5.5 6.5-5.5s5.7 1.9 6.5 5.5" />
      <path d="M19 8v6M16 11h6" />
    </svg>
  ),
  Share: () => (
    <svg {...base}>
      <path d="M12 3v12M7.5 7.5 12 3l4.5 4.5" />
      <path d="M5 12v7h14v-7" />
    </svg>
  ),
  Qr: () => (
    <svg {...base}>
      <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4z" />
      <path d="M14 14h2v2h-2zM18 14h2M14 18v2h2M18 18h2v2" />
    </svg>
  ),
  Pin: () => (
    <svg {...base}>
      <path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0C18.5 15.4 12 21 12 21z" />
      <circle cx="12" cy="10" r="2.3" />
    </svg>
  ),
  Close: () => (
    <svg {...base}>
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  ),
};

const mapUrl = (addr) => `https://map.naver.com/p/search/${encodeURIComponent(addr)}`;

export default function Card({ slug }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [toast, setToast] = useState("");
  const [photoFailed, setPhotoFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`/api/cards/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => alive && setCard(d))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  if (error) {
    return (
      <div className="bc-page">
        <div className="bc-empty">
          <p>명함을 찾을 수 없습니다.</p>
          <Link to="/" className="bc-link">아이닷메타 홈페이지로 이동</Link>
        </div>
      </div>
    );
  }
  if (!card) return <div className="bc-page" aria-busy="true" />;

  const co = card.company;
  const tel = card.phone.replace(/-/g, "");

  const share = async () => {
    const data = {
      title: `${card.name} ${card.title} · ${co.nameKo}`,
      text: `${co.nameKo} ${card.name} ${card.title}의 명함입니다.`,
      url: card.url,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
      await navigator.clipboard.writeText(card.url);
      setToast("명함 링크를 복사했습니다");
    } catch (e) {
      if (e && e.name === "AbortError") return; // 공유 창을 닫은 경우
      setToast(card.url);
    }
  };

  return (
    <div className="bc-page">
      <main className="bc-card">
        <header className="bc-head">
          <img src="/logo-imeta.png" alt={`${co.name} ${co.nameKo}`} className="bc-logo" />
          <p className="bc-tagline">{co.tagline}</p>
        </header>

        <section className="bc-id">
          {card.photo && !photoFailed ? (
            <img
              className="bc-avatar"
              src={card.photo}
              alt={`${card.name} 프로필 사진`}
              onError={() => setPhotoFailed(true)}
            />
          ) : (
            <div className="bc-avatar" aria-hidden="true">{card.familyName}</div>
          )}
          <h1 className="bc-name">
            {card.name}
            <span className="bc-title">{card.title}</span>
          </h1>
          <p className="bc-co">
            {co.nameKo} <span>{co.name}</span>
          </p>
        </section>

        <a className="bc-save" href={`/api/cards/${card.slug}.vcf`}>
          <Ico.UserPlus /> 연락처 저장
        </a>

        <nav className="bc-actions" aria-label="바로 연락하기">
          <a href={`tel:${tel}`}>
            <Icon.Phone width="20" height="20" aria-hidden="true" focusable="false" />
            전화
          </a>
          <a href={`sms:${tel}`}>
            <Ico.Chat />
            문자
          </a>
          <a href={`mailto:${card.email}`}>
            <Icon.Mail width="20" height="20" aria-hidden="true" focusable="false" />
            이메일
          </a>
          <a href={co.web}>
            <Icon.Globe width="20" height="20" aria-hidden="true" focusable="false" />
            홈페이지
          </a>
        </nav>

        <dl className="bc-info">
          <div>
            <dt>휴대폰</dt>
            <dd><a href={`tel:${tel}`}>{card.phone}</a></dd>
          </div>
          <div>
            <dt>이메일</dt>
            <dd><a href={`mailto:${card.email}`}>{card.email}</a></dd>
          </div>
          <div>
            <dt>본사</dt>
            <dd>
              <a href={mapUrl(co.address)} target="_blank" rel="noreferrer">
                {co.address} <Ico.Pin />
              </a>
            </dd>
          </div>
          <div>
            <dt>{co.labLabel}</dt>
            <dd>
              <a href={mapUrl(co.labAddress)} target="_blank" rel="noreferrer">
                {co.labAddress} <Ico.Pin />
              </a>
            </dd>
          </div>
        </dl>

        <section className="bc-about">
          <p>{co.desc}</p>
          <ul>
            {co.areas.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </section>

        <div className="bc-foot">
          <button type="button" onClick={() => setShowQr(true)}>
            <Ico.Qr /> QR 보여주기
          </button>
          <button type="button" onClick={share}>
            <Ico.Share /> 명함 공유
          </button>
        </div>
      </main>

      {showQr && (
        <div
          className="bc-qr"
          role="dialog"
          aria-modal="true"
          aria-label="명함 QR 코드"
          onClick={() => setShowQr(false)}
        >
          <div className="bc-qr-box" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="bc-qr-close" aria-label="닫기" onClick={() => setShowQr(false)}>
              <Ico.Close />
            </button>
            <img src={`/api/cards/${card.slug}/qr.svg`} alt={`${card.name} 명함 QR 코드`} />
            <p>
              카메라로 찍으면
              <br />
              이 명함이 열립니다
            </p>
            <a href={`/api/cards/${card.slug}/qr.png?dl=1`} className="bc-link">
              QR 이미지 저장
            </a>
          </div>
        </div>
      )}

      {toast && <div className="bc-toast" role="status">{toast}</div>}
    </div>
  );
}
